<template>
  <page
    :name="$t('Progetti')"
    :state="{ requirementsState, projectsState }"
    :loading="projectsState.updating || requirementsState.updating"
  >
    <template #header>
      {{ $t('Descrivi la tua esigenza, le sue caratteristiche e le specifiche tecniche e ti aiuteremo a trovare un fornitore affidabile tra le aziende della nostra rete di partner') }}
    </template>

    <pf-page-section no-body-wrapper v-if="projects.length">
      <pf-title h="2">{{ $t("Progetti presentati") }}</pf-title>

      <project-list variant="project" :projects="projects" />
    </pf-page-section>

    <pf-page-section no-body-wrapper>
      <pf-title h="2">{{ $t("Presenta un progetto") }}</pf-title>

      <pf-empty-state v-if="sending">
        <pf-empty-state-header :title="$t('Attendi solo pochi secondi...')" heading-level="h4">
          <template #icon>
            <pf-empty-state-icon>
              <pf-spinner />
            </pf-empty-state-icon>
          </template>
        </pf-empty-state-header>
      </pf-empty-state>

      <x-form v-else @submit="submit" :submit-text="$t('Invia')" :submit-help="$t('* Campo obbligatorio')">
        <pf-form component="div" style="max-width: 80ch">
          <form-group :name="$t('Nome del progetto')" required>
            <x-input
              id="projectName"
              v-model="project.nome"
              name="nome"
              type="text"
              required
              maxlength="250"
            />
          </form-group>
          <form-group :name="$t('Descrizione')" required>
            <pf-textarea
              id="projectDesc"
              v-model="project.descrizione"
              name="descrizione"
              required
              rows="5"
            />
          </form-group>
          <form-group :name="$t('Dominio')">
            <x-input
              id="projectDom"
              v-model="project.dominio"
              name="name"
              type="domain"
            />
          </form-group>
          <form-group :name="$t('Tempistiche indicative')">
            <pf-textarea
              id="projectTempi"
              v-model="project.tempistiche"
              name="descrizione"
              maxlength="250"
              rows="5"
            />
          </form-group>
          <form-group :name="$t('Budget indicativo')">
            <pf-textarea
              id="projectBdg"
              v-model="project.budget"
              name="descrizione"
              maxlength="250"
              rows="5"
            />
          </form-group>
          <form-group
            :name="$t('I tuoi contatti')"
            required
            :field-help="$t('Inserisci qui la tua email e il numero di telefono a cui essere contatto per richieste relative al progetto. Li invieremo solo al partner selezionato.')"
          >
            <pf-textarea
              id="projectContatti"
              v-model="project.contatti"
              name="contatti"
              maxlength="250"
              rows="5"
              required
            />
            <label><input type="checkbox" required name="authorization" /> {{ $t("Autorizzo l'invio dei miei dati al partner selezionato") }}</label>
          </form-group>
        </pf-form>

        <form-group :name="$t('Quali sono le skill richieste per la realizzazione del progetto?')" required>
          <!-- Input nascosto per mostrare che il campo skill è obbligatorio -->
          <input v-model="newProjectSelectedSkills" type="checkbox" required class="input-hidden">
          <pf-switch-list
            v-model="project.id_requirements"
            name="requisiti"
            :items="skills"
            label-field="nome"
            value-field="id"
          />
        </form-group>

        <form-group :name="$t('Con quali tecnologie dovrà essere sviluppato?')">
          <pf-switch-list
            v-model="project.id_requirements"
            name="requisiti[]"
            :items="techSkills"
            label-field="nome"
            value-field="id"
          />
        </form-group>

        <form-group :name="$t('In quale settore merceologico operi?')">
          <pf-switch-list
            v-model="project.id_requirements"
            name="requisiti[]"
            :items="businessSectors"
            label-field="nome"
            value-field="id"
          />
        </form-group>
      </x-form>
    </pf-page-section>
  </page>
</template>

<style lang="scss" scoped>
:deep(.pf-v6-c-switch) {
  min-width: 250px;
}
</style>

<script lang="ts" setup>
import { $t as _t } from '@/i18n';
import { ref, computed } from 'vue';
import { asyncComputedList } from '@common/asyncComputed';
import { useLoginStore } from '../store/login';
import { RequirementsResource, type Requirements, ProjectResource, type ProjectCreate } from '../resources';
import cloneDeep from 'lodash-es/cloneDeep';
import ProjectList from '../components/project-list.vue';
import PfSwitchList from '@common/PfSwitchList.vue';
import { useFunctionRunningState } from '@common/use';

const login = useLoginStore();

const emptyProject: ProjectCreate = {
  nome: '',
  id_requirements: [],
  skills: [],
  tech_skills: [],
  business_sectors: [],
  contatti: login.email,
};
const project = ref(cloneDeep(emptyProject));

function requirementsByType(tipo: Requirements['tipo']) {
  return requirements.value.filter(r => r.tipo === tipo);
}

const { state: requirementsState, result: requirements } = asyncComputedList(() => new RequirementsResource().get());
const { state: projectsState, result: projects } = asyncComputedList(() => new ProjectResource().get());

const skills = computed(() => requirementsByType('skill'));
const techSkills = computed(() => requirementsByType('tech skill'));
const businessSectors = computed(() => requirementsByType('business sector'));

const newProjectSelectedSkills = computed(() => (skills.value.filter(r => project.value.id_requirements.includes(r.id))).length > 0);

const { isRunning: sending, run: submit } = useFunctionRunningState(async() => {
  const projectRes = new ProjectResource();
  await projectRes.save(project.value);

  project.value = cloneDeep(emptyProject);
  projectsState.update();
}, { preventMultiple: true });
</script>
